import {
  addQueryParams,
  removeQueryParams,
  Skeleton,
  useRouter,
} from '@main/core-ui';
import { isValidActionType } from '@main/datamap-types';
import { DisplayActions, SanitizeHtml } from '@main/pc-core-components';
import { endpoints, privacyCenterCustomizableMessages } from '@main/pc-types';
import TakeControlConfirm from '@main/privacy-center/src/components/TakeControlConfirm';
import ActiveDataSubject from '@main/privacy-center/src/containers/ActiveDataSubject';
import { selectActiveDataSubject } from '@main/privacy-center/src/containers/ActiveDataSubject/selectors';
import {
  selectCoreIdentifier,
  selectIsLoggedIn,
  selectIsLoggingIn,
  selectRequiredPrivacyCenter,
} from '@main/privacy-center/src/containers/App/selectors';
import { Login } from '@main/privacy-center/src/containers/Login';
import { useAuthorizationUrl } from '@main/privacy-center/src/containers/Login/generateAuthorizationUrl';
import {
  setSelectedAction,
  unsetSelectedAction,
} from '@main/privacy-center/src/containers/TokenLogin/slice';
import { buildUseDhMutation } from '@main/privacy-center/src/hooks';
import { LanguageKey } from '@transcend-io/internationalization';
import { RequestAction } from '@transcend-io/privacy-types';
import React, { forwardRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { usePartition } from '../../hooks/usePartition';
import { unsetActiveDataSubject } from '../ActiveDataSubject/slice';
import { writeOauthStateToLocalStorage } from '../TokenLogin/helpers';
import { formToRequestIdentifiers } from './formToRequestIdentifiers';
import { takeControlMessages } from './messages';
import { selectDisabledActions, selectTakeControlModalName } from './selectors';
import {
  ButtonWrapper,
  ClassOverrides,
  DataSubjectValueWrapper,
  DataSubjectWrapper,
  DescriptionWrapper,
  HeaderWrapper,
  ModalWrapper,
  SelectSubjectModalWrapper,
  StyledModal,
  TakeControlModalWrapper,
  TakeControlWrapper,
  TextWrapper,
  TitleWrapper,
} from './wrappers';
import { syncAllConsent, syncSaleOfInfoConsent } from './xdi';

const useMakeDataSubjectRequest = buildUseDhMutation(
  endpoints.makeDataSubjectRequest,
);

export interface TakeControlProps {
  /** Explicit onExited function with context of modal button ref */
  onExited: () => void;
}

/**
 * Original component displaying actions.
 */
export const ActionsTakeControl = forwardRef<
  HTMLButtonElement,
  TakeControlProps
>(({ onExited }, ref) => {
  const { formatMessage, locale } = useIntl();
  const [showSuccess, setShowSuccess] = useState(false);
  const { queryParams, redirect, location } = useRouter();
  const [makeDataSubjectRequest, { error }] = useMakeDataSubjectRequest({
    refetchQueries: ['RequestHistory', 'RequestHistoryActiveCount'],
  });
  const [{ selectedPartitionOption, partitionOptions, showPartition }] =
    usePartition();
  const coreIdentifier = useSelector(selectCoreIdentifier);
  const activeDataSubject = useSelector(selectActiveDataSubject);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isLoggingIn = useSelector(selectIsLoggingIn);
  const takeControlModalName = useSelector(selectTakeControlModalName);
  const disabledActions = useSelector(selectDisabledActions);
  const privacyCenter = useSelector(selectRequiredPrivacyCenter);
  const dispatch = useDispatch();
  const authorizationUrlPayload = useAuthorizationUrl(activeDataSubject);

  // We need at least one action and one subject
  if (!activeDataSubject) {
    return null;
  }

  const hideModal = (): void => {
    dispatch(unsetActiveDataSubject);
    dispatch(unsetSelectedAction);
    if (showSuccess) {
      redirect(removeQueryParams(location, ['action', 'modal']));
    } else {
      redirect(removeQueryParams(location, ['modal']));
    }
    setShowSuccess(false);
  };

  const title = (
    <TextWrapper>
      {activeDataSubject.disabledInstructions ? (
        <DescriptionWrapper>
          <SanitizeHtml
            html={formatMessage(activeDataSubject.disabledInstructions)}
          />
        </DescriptionWrapper>
      ) : (
        <>
          <TitleWrapper>
            {formatMessage(takeControlMessages.title)}
          </TitleWrapper>
          <DescriptionWrapper>
            <SanitizeHtml
              html={formatMessage(
                privacyCenterCustomizableMessages.takeControlDescription,
              )}
            />
          </DescriptionWrapper>
          <DataSubjectWrapper>
            {isLoggedIn
              ? formatMessage(takeControlMessages.dataSubjectLoggedIn, {
                  dataSubject: (
                    <DataSubjectValueWrapper>
                      {formatMessage(activeDataSubject.title).toLowerCase()}
                    </DataSubjectValueWrapper>
                  ),
                })
              : formatMessage(takeControlMessages.dataSubjectNotLoggedIn, {
                  verifyIdentityLogin: <Login />,
                })}
          </DataSubjectWrapper>
          {showPartition &&
            selectedPartitionOption &&
            partitionOptions.length > 0 && (
              <DataSubjectWrapper>
                {formatMessage(takeControlMessages.partitionLoggedIn, {
                  partition: selectedPartitionOption.label,
                })}
              </DataSubjectWrapper>
            )}
        </>
      )}
    </TextWrapper>
  );

  // Determine the selected action
  const rawActionType = queryParams.action || '';
  const actionType =
    isLoggedIn && isValidActionType(rawActionType) ? rawActionType : undefined;
  const selectedAction = activeDataSubject.actions.find(
    (a) => a.type === actionType,
  );

  return (
    <TakeControlWrapper>
      <TakeControlModalWrapper>
        <ClassOverrides />
        {privacyCenter.showPrivacyRequestButton && (
          <ButtonWrapper
            buttonType="primary"
            id={`modal-${takeControlModalName}`}
            onClick={() =>
              redirect(
                addQueryParams(location, { modal: takeControlModalName }),
              )
            }
            ref={ref}
          >
            {formatMessage(takeControlMessages.takeControl)}
          </ButtonWrapper>
        )}
        <StyledModal
          width="fit-content"
          centered
          onHide={hideModal}
          onExited={onExited}
          show={queryParams.modal === 'select-subject'}
          label={takeControlMessages.dataSubjectSelectLabel}
        >
          <SelectSubjectModalWrapper>
            <ActiveDataSubject />
          </SelectSubjectModalWrapper>
        </StyledModal>
        <StyledModal
          width="fit-content"
          centered
          onHide={hideModal}
          onExited={onExited}
          show={activeDataSubject && queryParams.modal === 'take-control'}
          label={takeControlMessages.title}
        >
          {isLoggingIn ? (
            <ModalWrapper>
              {title}
              <Skeleton paragraph={false} />
              <Skeleton paragraph />
              <Skeleton paragraph />
            </ModalWrapper>
          ) : selectedAction ? (
            <TakeControlConfirm
              coreIdentifier={coreIdentifier?.value || ''}
              activeDataSubjectId={activeDataSubject.id}
              goBack={() => {
                setShowSuccess(false);
                redirect(removeQueryParams(location, ['action']));
              }}
              confirmRequest={({
                captcha,
                subjectType,
                type,
                postCompileStatus,
                region,
                details,
                ...rest
              }) =>
                makeDataSubjectRequest && // this should always be defined at this point
                makeDataSubjectRequest({
                  variables: {
                    input: {
                      type,
                      subjectType,
                      details,
                      region,
                      ...(postCompileStatus ? { postCompileStatus } : {}),
                      requestIdentifiers: formToRequestIdentifiers(rest),
                      locale: locale as LanguageKey,
                      captcha,
                      partitionKey: selectedPartitionOption?.value,
                    },
                  },
                }).then(({ data }) => {
                  if (data) {
                    const { consentManagers } = privacyCenter;
                    consentManagers.forEach(({ id, configuration }) => {
                      if (
                        type === RequestAction.TrackingOptOut &&
                        configuration.syncEndpoint
                      ) {
                        syncAllConsent(configuration.syncEndpoint, id, false);
                      }

                      if (
                        type === RequestAction.SaleOptOut &&
                        configuration.syncEndpoint
                      ) {
                        syncSaleOfInfoConsent(
                          configuration.syncEndpoint,
                          id,
                          false,
                        );
                      }
                    });

                    setShowSuccess(true);
                  }
                })
              }
              subjectType={activeDataSubject.type}
              submittedSuccessfully={showSuccess}
              error={error}
              action={selectedAction}
            />
          ) : (
            <ModalWrapper>
              {title}
              {!activeDataSubject.disabledInstructions && (
                <>
                  <HeaderWrapper>
                    {formatMessage(takeControlMessages.header)}
                  </HeaderWrapper>
                  <DisplayActions
                    // normally disabledActions is sufficient and we don't need to check
                    // isLoggedIn, however a corrupted redux state can cause a state where
                    // the user is logged out and cannot select actions. this check
                    // prevents that invalid state
                    disabledActions={isLoggedIn ? disabledActions : []}
                    actionButtons={activeDataSubject.actions}
                    selectAction={(selectedAction) => {
                      // If active data subject has oauth, navigate to OAuth route, otherwise
                      // immediately navigate to process dsr
                      if (authorizationUrlPayload && !isLoggedIn) {
                        dispatch(
                          setSelectedAction({
                            selectedAction,
                            oAuthState: authorizationUrlPayload.state,
                            selectedLocale: locale as LanguageKey,
                          }),
                        );

                        writeOauthStateToLocalStorage(
                          authorizationUrlPayload.state,
                        );
                        window.location.replace(
                          authorizationUrlPayload.url.href,
                        );
                      } else if (!authorizationUrlPayload && !isLoggedIn) {
                        redirect(
                          addQueryParams(location, {
                            modal: 'log-in',
                            action: selectedAction,
                          }),
                        );
                      } else {
                        redirect(
                          addQueryParams(location, {
                            action: selectedAction,
                          }),
                        );
                      }
                    }}
                  />
                </>
              )}
            </ModalWrapper>
          )}
        </StyledModal>
      </TakeControlModalWrapper>
    </TakeControlWrapper>
  );
});

export default ActionsTakeControl;
